import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { approveOrder } from '../../api/orders';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { ChargeType, Order } from '../../types/api';

export function ApproveOrderModal({
  order,
  open,
  onClose,
  onApproved,
}: {
  order: Order;
  open: boolean;
  onClose: () => void;
  onApproved?: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [poNumber, setPoNumber] = useState('');
  const [chargeType, setChargeType] = useState<ChargeType>('ONE_OFF');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => approveOrder(order.id, { poNumber, chargeType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      queryClient.invalidateQueries({ queryKey: ['orders-queue'] });
      showToast(`Order "${order.reference}" approved.`);
      setPoNumber('');
      onClose();
      onApproved?.();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not approve the order.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!poNumber.trim()) {
      setError('A PO number is required to approve.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Approve "${order.reference}"`}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="approve-order-form" type="submit" isLoading={mutation.isPending}>
            Approve order
          </Button>
        </>
      }
    >
      <form id="approve-order-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}

        <Input
          label="PO number"
          required
          autoFocus
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
        />

        <Select
          label="Charge type"
          value={chargeType}
          onChange={(e) => setChargeType(e.target.value as ChargeType)}
          hint={
            chargeType === 'MANAGED'
              ? "Term and billing frequency are set on each catalog item by your MSP - not chosen here."
              : undefined
          }
        >
          <option value="ONE_OFF">One-off - billed as a single charge</option>
          <option value="MANAGED">Managed - billed in instalments over a term</option>
        </Select>
      </form>
    </Modal>
  );
}
